// frontend/src/components/engineer/widgets/AirportSearchWidget.tsx
// Airport Search Widget with HORIZONTAL Scrolling Cards
// PHOENICIAN TECHNICAL SERVICES LLC

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  airportService, 
  Airport, 
  UAE_AIRPORT_CODES, 
  GCC_AIRPORT_CODES, 
  POPULAR_DESTINATION_CODES 
} from '../../../services/airport.service';
import styles from './AirportSearchWidget.module.css';

export interface AirportSearchWidgetProps {
  label: string;
  hint: string;
  selectedAirport: Airport | null;
  onAirportSelected: (airport: Airport | null) => void;
  showUaeAirportsFirst?: boolean;
  isRequired?: boolean;
  errorText?: string;
}

export const AirportSearchWidget: React.FC<AirportSearchWidgetProps> = ({
  label,
  hint,
  selectedAirport,
  onAirportSelected,
  showUaeAirportsFirst = false,
  isRequired = true,
  errorText
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchResults, setSearchResults] = useState<Airport[]>([]);
  const [popularAirports, setPopularAirports] = useState<Airport[]>([]);
  const [showAllResults, setShowAllResults] = useState<boolean>(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Initialize airport service
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        await airportService.loadAirports();
        
        if (showUaeAirportsFirst) {
          setPopularAirports(airportService.getUaeAirports());
        } else {
          setPopularAirports(airportService.getPopularDestinations());
        }
      } catch (e) {
        console.error('Error loading airports:', e);
      } finally {
        setIsLoading(false);
      }
    };
    
    init();
  }, [showUaeAirportsFirst]);
  
  // Debounced search
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowAllResults(false);
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    if (value.length < 2) {
      setSearchResults([]);
      return;
    }
    
    debounceRef.current = setTimeout(() => {
      const results = airportService.searchAirports(value, 20);
      setSearchResults(results);
    }, 300);
  }, []);
  
  // Handle airport selection
  const handleSelectAirport = useCallback((airport: Airport) => {
    setSearchQuery('');
    onAirportSelected(airport);
    setSearchResults([]);
    setShowAllResults(false);
  }, [onAirportSelected]);
  
  // Clear selection
  const handleClear = useCallback(() => {
    setSearchQuery('');
    onAirportSelected(null);
    setSearchResults([]);
    setShowAllResults(false);
    inputRef.current?.focus();
  }, [onAirportSelected]);
  
  // Get badge color based on airport type
  const getAirportBadgeColor = useCallback((airport: Airport): string => {
    if (UAE_AIRPORT_CODES.includes(airport.iataCode)) return styles.badgeUae;
    if (GCC_AIRPORT_CODES.includes(airport.iataCode)) return styles.badgeGcc;
    if (POPULAR_DESTINATION_CODES.includes(airport.iataCode)) return styles.badgePopular;
    return styles.badgeDefault;
  }, []);

  // Scroll handlers for horizontal navigation
  const scrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  }, []);

  const scrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  }, []);
  
  // Determine what to show
  const displayAirports = useMemo(() => {
    if (searchResults.length > 0) return searchResults;
    return popularAirports;
  }, [searchResults, popularAirports]);
  
  const hasSearchResults = searchResults.length > 0;
  const hasQuery = searchQuery.length >= 2;
  const showNoResults = hasQuery && !hasSearchResults;

  // Show limited or all results
  const visibleAirports = showAllResults ? displayAirports : displayAirports.slice(0, 8);
  const hasMore = displayAirports.length > 8 && !showAllResults;
  
  return (
    <div className={styles.container} ref={containerRef}>
      {/* Label */}
      <div className={styles.labelRow}>
        <label className={styles.label}>{label}</label>
        {isRequired && <span className={styles.required}>*</span>}
      </div>
      
      {/* Selected Airport Display - Show when selected */}
      {selectedAirport && selectedAirport.iataCode ? (
        <div className={styles.selectedCard}>
          <div className={styles.selectedContent}>
            <span className={`${styles.selectedBadge} ${getAirportBadgeColor(selectedAirport)}`}>
              {selectedAirport.iataCode}
            </span>
            <div className={styles.selectedInfo}>
              <span className={styles.selectedCity}>{selectedAirport.city}</span>
              <span className={styles.selectedName}>{selectedAirport.name}</span>
              <span className={styles.selectedCountry}>{selectedAirport.country}</span>
            </div>
          </div>
          <button type="button" onClick={handleClear} className={styles.changeButton}>
            Change
          </button>
        </div>
      ) : (
        <>
          {/* Search Input */}
          <div className={styles.searchWrapper}>
            <span className={styles.searchIcon}>✈️</span>
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={hint}
              className={`${styles.searchInput} ${errorText ? styles.inputError : ''}`}
            />
            {searchQuery && (
              <button type="button" onClick={() => setSearchQuery('')} className={styles.clearButton}>
                ✕
              </button>
            )}
            {isLoading && <span className={styles.spinner}></span>}
          </div>
          
          {/* Error Text */}
          {errorText && (
            <div className={styles.errorRow}>
              <span className={styles.errorIcon}>⚠️</span>
              <span className={styles.errorText}>{errorText}</span>
            </div>
          )}
          
          {/* No Results Message */}
          {showNoResults && (
            <div className={styles.noResults}>
              <span>🔍</span>
              <p>No airports found for "{searchQuery}"</p>
              <small>Try searching by city, airport name, or IATA code</small>
            </div>
          )}
          
          {/* Section Header */}
          {!showNoResults && displayAirports.length > 0 && (
            <div className={styles.sectionHeader}>
              <span className={styles.sectionTitle}>
                {hasSearchResults ? `Search Results (${displayAirports.length})` : (showUaeAirportsFirst ? 'UAE Airports' : 'Popular Destinations')}
              </span>
              {displayAirports.length > 4 && (
                <div className={styles.scrollButtons}>
                  <button type="button" onClick={scrollLeft} className={styles.scrollBtn}>
                    ←
                  </button>
                  <button type="button" onClick={scrollRight} className={styles.scrollBtn}>
                    →
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* HORIZONTAL SCROLLING AIRPORT CARDS */}
          {!showNoResults && visibleAirports.length > 0 && (
            <div className={styles.cardsWrapper}>
              <div className={styles.cardsContainer} ref={scrollContainerRef}>
                {visibleAirports.map((airport) => (
                  <button
                    key={airport.iataCode}
                    type="button"
                    onClick={() => handleSelectAirport(airport)}
                    className={styles.airportCard}
                  >
                    <span className={`${styles.cardBadge} ${getAirportBadgeColor(airport)}`}>
                      {airport.iataCode}
                    </span>
                    <span className={styles.cardCity}>{airport.city}</span>
                    <span className={styles.cardCountry}>{airport.country}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Show More Button */}
          {hasMore && (
            <button 
              type="button" 
              onClick={() => setShowAllResults(true)} 
              className={styles.showMoreBtn}
            >
              Show all {displayAirports.length} airports ↓
            </button>
          )}

          {/* Grid View for All Results */}
          {showAllResults && displayAirports.length > 8 && (
            <div className={styles.gridContainer}>
              {displayAirports.slice(8).map((airport) => (
                <button
                  key={airport.iataCode}
                  type="button"
                  onClick={() => handleSelectAirport(airport)}
                  className={styles.gridCard}
                >
                  <span className={`${styles.gridBadge} ${getAirportBadgeColor(airport)}`}>
                    {airport.iataCode}
                  </span>
                  <div className={styles.gridInfo}>
                    <span className={styles.gridCity}>{airport.city}</span>
                    <span className={styles.gridCountry}>{airport.country}</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {showAllResults && (
            <button 
              type="button" 
              onClick={() => setShowAllResults(false)} 
              className={styles.showLessBtn}
            >
              Show less ↑
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default AirportSearchWidget; 